import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

const BANCARD_JS = {
  staging: 'https://vpos.infonet.com.py:8888/checkout/javascript/dist/bancard-checkout-4.0.0.js',
  production: 'https://vpos.infonet.com.py/checkout/javascript/dist/bancard-checkout-4.0.0.js',
};

const STYLES = {
  'input-background-color': '#f6f8fa',
  'input-text-color': '#1c1917',
  'input-border-color': '#dce9e2',
  'input-placeholder-color': '#78716c',
  'input-error-color': '#df1b41',
  'input-cvv-color': '#1c1917',
  'button-background-color': '#264639',
  'button-text-color': '#ffffff',
  'button-border-color': '#264639',
  'form-background-color': '#ffffff',
  'form-border-color': '#dce9e2',
  'header-background-color': '#dce9e2',
  'header-text-color': '#1c1917',
  'hr-border-color': '#dce9e2',
  'label-kyc-text-color': '#30313d',
  'label-text-color': '#30313d',
  'tab-main-color': '#264639',
  'tab-background-color': '#f6f8fa',
  'floating-placeholder': 'true',
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.Bancard) return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('load')));
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('load'));
    document.head.appendChild(script);
  });
}

/** Mounts the Bancard "pago ocasional" iframe. onResult(message, details) fires when Bancard reports the outcome. */
export function BancardFrame({ processId, env, loading, onReady, onResult, onError }) {
  const cb = useRef({ onReady, onResult, onError });
  cb.current = { onReady, onResult, onError };

  useEffect(() => {
    let cancelled = false;
    const src = BANCARD_JS[env] || BANCARD_JS.staging;
    loadScript(src)
      .then(() => {
        if (cancelled) return;
        if (!window.Bancard?.Checkout) throw new Error('init');
        window.Bancard.Checkout.createForm('bancard-iframe-container', processId, {
          styles: STYLES,
          responseHandler: (data) => cb.current.onResult?.(data?.message, data?.details),
        });
        cb.current.onReady?.();
      })
      .catch((e) => {
        if (cancelled) return;
        cb.current.onError?.(e.message === 'load'
          ? 'No se pudo cargar el formulario de pago de Bancard. Verificá tu conexión e intentá de nuevo.'
          : 'Error al inicializar el formulario de Bancard. Recargá la página e intentá de nuevo.');
      });
    return () => {
      cancelled = true;
      const container = document.getElementById('bancard-iframe-container');
      if (container) container.innerHTML = '';
    };
  }, [processId, env]);

  return (
    <div className="max-w-lg w-full bg-white dark:bg-[#1C1917] rounded-2xl shadow-xl overflow-hidden min-h-[420px] relative" data-testid="bancard-frame">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-[#1C1917] z-10" data-testid="bancard-frame-loading">
          <Loader2 className="w-10 h-10 text-[#264639] animate-spin mb-3" />
          <p className="text-sm text-[#57534E] dark:text-[#A8A29E]">Cargando formulario de pago...</p>
        </div>
      )}
      <div id="bancard-iframe-container" className="w-full min-h-[420px]" style={{ minWidth: 320 }} />
    </div>
  );
}
